import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import FormatBar from '../format_bar/formatBar';
import './adminOm.css';

const AdminOm = () => {
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const engEditorRef = useRef(null);
  
  // Auth state
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Om os states
  const [psykologer, setPsykologer] = useState([]);
  const [selectedPsykolog, setSelectedPsykolog] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Rich text editor states
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false,
    image: false
  });
  
  const [engActiveFormats, setEngActiveFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false,
    image: false
  });
  
  // Form states
  const [editedPsykolog, setEditedPsykolog] = useState({
    id: '',
    navn: '',
    titler: '',
    engTitler: '',
    beskrivelse: '',
    langBeskrivelse: '',
    engBeskrivelse: '',
    speciale: '',
    engSpeciale: '',
    email: '',
    telefon: '',
    engEmail: '',
    engTelefon: '',
    billede: '',
    billedeUrl: '',
    link: '',
    punkter: [],
    engPunkter: []
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [newBulletPoint, setNewBulletPoint] = useState('');
  const [newEngBulletPoint, setNewEngBulletPoint] = useState('');

  // Tilføj nye refs til MutationObservers
  const danishObserver = useRef(null);
  const englishObserver = useRef(null);

  // Setup observers når komponenten mountes
  useEffect(() => {
    const observerConfig = {
      childList: true,
      characterData: true,
      subtree: true
    };

    // Observer for dansk editor
    danishObserver.current = new MutationObserver(() => {
      if (editorRef.current) {
        setEditedPsykolog(prev => ({
          ...prev,
          langBeskrivelse: editorRef.current.innerHTML
        }));
      }
    });

    // Observer for engelsk editor
    englishObserver.current = new MutationObserver(() => {
      if (engEditorRef.current) {
        setEditedPsykolog(prev => ({
          ...prev,
          engBeskrivelse: engEditorRef.current.innerHTML
        }));
      }
    });

    return () => {
      danishObserver.current?.disconnect();
      englishObserver.current?.disconnect();
    };
  }, []);

  // Start observation når redigering begynder
  useEffect(() => {
    if (isEditing || isCreating) {
      if (editorRef.current) {
        danishObserver.current?.observe(editorRef.current, {
          childList: true,
          characterData: true,
          subtree: true
        });
      }
      if (engEditorRef.current) {
        englishObserver.current?.observe(engEditorRef.current, {
          childList: true,
          characterData: true,
          subtree: true
        });
      }
    }

    return () => {
      danishObserver.current?.disconnect();
      englishObserver.current?.disconnect();
    };
  }, [isEditing, isCreating]);

  // Check auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setIsLoading(false);
      if (!user) {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load content
  useEffect(() => {
    const fetchPsykologer = async () => {
      try {
        const omDocRef = doc(db, 'content', 'om');
        const omDoc = await getDoc(omDocRef);
        
        if (omDoc.exists()) {
          // Ensure all psykologer have engPunkter
          const loadedPsykologer = omDoc.data().psykologer || [];
          const normalizedPsykologer = loadedPsykologer.map(psykolog => ({
            ...psykolog,
            punkter: psykolog.punkter || [],
            engPunkter: psykolog.engPunkter || [],
            engEmail: psykolog.engEmail || '',
            engTelefon: psykolog.engTelefon || ''
          }));
          
          setPsykologer(normalizedPsykologer);
        } else {
          // Hvis dokument ikke findes, initialiser med eksisterende data fra komponenten
          const defaultPsykologer = [
            {
              id: 'maria',
              navn: 'Maria Højer Nannestad',
              titler: 'Autoriseret Psykolog, specialist i klinisk børne- og ungepsykologi samt psykoterapi med børn og med voksne. Specialistgodkendt supervisor.',
              beskrivelse: '',
              langBeskrivelse: 'Jeg er autoriseret psykolog med speciale i traumer og vold i nære relationer. Jeg bor i Københavns området sammen med min mand og vores datter på 17 år og vores søn på 22 år.\n\nJeg arbejder med børn i alle aldersgrupper, fra spædbørn til unge. Jeg holder meget af, at lære de børn og unge at kende som kommer i vores klinik, og blive klogere på hvem de er, med de sårbarheder der ligger bag henvendelsen, men også med de store ressourcer og kompetencer de også altid besidder. Jeg oplever det som en daglig gave at få lov til at være vidne til store og små udviklingsprocesser, og være med til at give stemme og forståelser til de alvorlige og komplicerede oplevelser, som mange børn og unge ellers ofte har gået alene med. At være børnepsykolog er ensbetydende med at skulle være kreativ og legende, fordi det er børns sprog og helt essentielt for børns væren i verden. Dertil kan det være afgørende at vælge legende og kreativ når man skal finde måder at være sammen om noget som kan være meget smertefuldt eller svært at udtrykke via "almindelig" samtale. Jeg har også positiv erfaring med, at lave traumebehandling med voksne der har levet med vold, enten da de selv var børn eller i parforhold i deres voksen liv. At skabe tryghed er en væsentligt del af min hverdag, uanset om det er voksne, eller børn der kommer hos mig. Det kan for mange føles lidt (eller meget) skræmmende og overvældende, at skulle opsøge terapi. Derfor er det min fornemste opgave, at skabe mest muligt tryghed i samtaler om emner, der kan føles meget utrygge og indimellem decideret angstprovokerende. Tryghed og sammenhørighed er afgørende for udvikling og bevægelse, og det er derfor mit store håb, at alle de mennesker jeg møder, har en oplevelse af, at jeg har gjort mig umage med at skabe en behagelig og tryg stemning, som har gjort det muligt at dele svære og nogle gange skamfulde tanker og følelser. Det er vigtigt for mig, at der skal være plads til det allermest smertelige, samtidigt med at alle går ud af døren, og kan føle sig som okay børn, unge, forældre og voksne mennesker.',
              speciale: 'Speciale i vold i nære relationer og tager udelukkende forløb med børn, unge og voksne, der har levet med vold i familien, fx i forbindelse med ophold på krisecenter.',
              email: 'maria@psykologiskperspektiv.dk',
              telefon: '23 27 65 95',
              billede: 'maria.avif',
              billedeUrl: '/media/om/maria.avif',
              link: '/om/maria',
              punkter: [],
              engPunkter: [],
              engEmail: '',
              engTelefon: ''
            },
            {
              id: 'susanne',
              navn: 'Susanne Wederkinck Nielsen',
              titler: 'Autoriseret psykolog, specialist i klinisk børnepsykologi og psykoterapi med børn. Specialistgodkendt supervisor.',
              beskrivelse: '',
              langBeskrivelse: 'Susanne er en erfaren psykolog med speciale i Mentaliseringsbaseret terapi. Hun har arbejdet med børn, unge og familier i mere end 15 år og er specialistgodkendt supervisor.\n\nHendes tilgang er præget af empati, faglighed og et dybt engagement i sine klienter. Hun har en særlig evne til at skabe trygge rammer, hvor selv svære emner kan bearbejdes på en konstruktiv måde.',
              speciale: 'Speciale i Mentaliseringsbaseret terapi med børn, unge og familier samt supervision og undervisning i MBT.',
              email: 'susanne@psykologiskperspektiv.dk',
              telefon: '61 12 66 85',
              billede: 'susanne.avif',
              billedeUrl: '/media/om/susanne.avif',
              link: '/om/susanne',
              punkter: [],
              engPunkter: [],
              engEmail: '',
              engTelefon: ''
            },
            {
              id: 'sidsel',
              navn: 'Sidsel Linnet',
              titler: 'Autoriseret Psykolog, Registered Clinical Psychologist. På vej til at blive specialist i Klinisk børne og Ungepsykologi.',
              beskrivelse: '',
              langBeskrivelse: 'Sidsel har arbejdet med børn og unge i mange år, både i Danmark og i New Zealand, hvor hun i en årrække havde egen privat praksis.\n\nMed sin baggrund som Registered Clinical Psychologist har hun en bred erfaring med forskellige terapeutiske tilgange, herunder MBT-A, DAT, KAT og ACT. Hendes internationale erfaring gør hende særligt egnet til at arbejde med engelsktalende klienter med expat-baggrund.\n\nSidsels arbejde med vold i nære relationer og hendes engagement i supervision og undervisning i MBT afspejler hendes dedikation til at hjælpe mennesker i svære livssituationer og til at styrke fagligheden hos kolleger.',
              speciale: 'Speciale i unge.',
              email: 'sidsel@psykologiskperspektiv.dk',
              telefon: '53 85 11 75',
              billede: 'sidsel.avif',
              billedeUrl: '/media/om/sidsel.avif',
              link: '/om/sidsel',
              punkter: [
                'engelsktalende klienter med expat-baggrund',
                'vold i nære relationer',
                'supervision og undervisning i MBT'
              ],
              engPunkter: [],
              engEmail: '',
              engTelefon: ''
            }
          ];
          
          setPsykologer(defaultPsykologer);
          
          // Gem default data i Firestore
          await setDoc(doc(db, 'content', 'om'), {
            psykologer: defaultPsykologer,
            lastUpdated: new Date()
          });
        }
      } catch (error) {
        console.error('Fejl ved hentning af psykologer:', error);
        setMessage({
          text: 'Der opstod en fejl ved indlæsning af psykologer',
          type: 'error'
        });
      }
    };

    if (user) {
      fetchPsykologer();
    }
  }, [user]);

  // Tilføj event listener for selection change
  useEffect(() => {
    const handleSelectionChange = () => {
      // Kun tjek formateringer hvis vi er i redigeringstilstand
      if (!isEditing && !isCreating) return;
      
      // Find ud af hvilken editor der har fokus
      const activeElement = document.activeElement;
      const isDanishEditor = editorRef.current && editorRef.current.contains(activeElement);
      const isEnglishEditor = engEditorRef.current && engEditorRef.current.contains(activeElement);
      
      if (isDanishEditor) {
        checkActiveFormats(false);
      } else if (isEnglishEditor) {
        checkActiveFormats(true);
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [isEditing, isCreating]);

  const handleEditorFocus = (isEnglish = false) => {
    const editor = isEnglish ? engEditorRef.current : editorRef.current;
    if (editor) {
      editor.focus();
      checkActiveFormats(isEnglish);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedPsykolog(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const normalizeWhitespace = (html) => {
    if (!html) return '';
    
    let normalized = html;
    
    // Bevar linjeskift mellem blokke men fjern overflødige
    normalized = normalized.replace(/(\r\n|\n|\r)/gm, '\n');
    normalized = normalized.replace(/\n{3,}/g, '\n\n');
    
    // Normaliser mellemrum inden i tekst-noder (ikke mellem tags)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = normalized;
    
    const normalizeTextNode = (node) => {
      if (node.nodeType === 3) { // Text node
        node.textContent = node.textContent.replace(/\s+/g, ' ');
      } else if (node.nodeType === 1) { // Element node
        Array.from(node.childNodes).forEach(normalizeTextNode);
      }
    };
    
    normalizeTextNode(tempDiv);
    normalized = tempDiv.innerHTML;
    
    // Fjern tomme tags men bevar br tags
    normalized = normalized.replace(/<(p|div|h[1-6]|li)>\s*<\/\1>/g, '');
    normalized = normalized.replace(/<br\s*\/?>\s*<br\s*\/?>/g, '<br>');
    
    return normalized.trim();
  };

  const handleHtmlContentChange = (e, isEnglish = false) => {
    checkActiveFormats(isEnglish);
  };

  const handleKeyDown = (e, isEnglish = false) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      document.execCommand('insertLineBreak', false);
    }
  };

  const handleBulletChange = (index, value) => {
    const updatedPunkter = [...editedPsykolog.punkter];
    updatedPunkter[index] = value;
    setEditedPsykolog(prev => ({
      ...prev,
      punkter: updatedPunkter
    }));
  };

  const handleEngBulletChange = (index, value) => {
    const updatedEngPunkter = [...(editedPsykolog.engPunkter || [])];
    updatedEngPunkter[index] = value;
    setEditedPsykolog(prev => ({
      ...prev,
      engPunkter: updatedEngPunkter
    }));
  };

  const handleAddBullet = () => {
    if (newBulletPoint.trim()) {
      setEditedPsykolog(prev => ({
        ...prev,
        punkter: [...prev.punkter, newBulletPoint.trim()]
      }));
      setNewBulletPoint('');
    }
  };

  const handleAddEngBullet = () => {
    if (newEngBulletPoint.trim()) {
      setEditedPsykolog(prev => ({
        ...prev,
        engPunkter: [...(prev.engPunkter || []), newEngBulletPoint.trim()]
      }));
      setNewEngBulletPoint('');
    }
  };

  const handleRemoveBullet = (index) => {
    const updatedPunkter = [...editedPsykolog.punkter];
    updatedPunkter.splice(index, 1);
    setEditedPsykolog(prev => ({
      ...prev,
      punkter: updatedPunkter
    }));
  };

  const handleRemoveEngBullet = (index) => {
    const updatedEngPunkter = [...(editedPsykolog.engPunkter || [])];
    updatedEngPunkter.splice(index, 1);
    setEditedPsykolog(prev => ({
      ...prev,
      engPunkter: updatedEngPunkter
    }));
  };

  const handleEditPsykolog = (psykolog) => {
    setEditedPsykolog(psykolog);
    setSelectedPsykolog(psykolog);
    setIsEditing(true);
    
    // Lad useEffect håndtere initialiseringen af indholdet
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = psykolog.langBeskrivelse || '';
      }
      if (engEditorRef.current) {
        engEditorRef.current.innerHTML = psykolog.engBeskrivelse || '';
      }
    }, 0);
  };

  const handleCreatePsykolog = () => {
    const newPsykolog = {
      id: '',
      navn: '',
      titler: '',
      engTitler: '',
      beskrivelse: '',
      langBeskrivelse: '',
      engBeskrivelse: '',
      speciale: '',
      engSpeciale: '',
      email: '',
      telefon: '',
      engEmail: '',
      engTelefon: '',
      billede: '',
      billedeUrl: '',
      link: '',
      punkter: [],
      engPunkter: []
    };
    
    setEditedPsykolog(newPsykolog);
    setIsCreating(true);
    
    // Initialiser tomme editorer
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = '';
      }
      if (engEditorRef.current) {
        engEditorRef.current.innerHTML = '';
      }
    }, 0);
  };

  const handleDeletePsykolog = async (psykologId) => {
    if (window.confirm('Er du sikker på, at du vil slette denne psykolog?')) {
      try {
        // Find psykolog for at få billedet
        const psykologToDelete = psykologer.find(p => p.id === psykologId);
        
        // Slet billede hvis det findes og ikke er et standard billede
        if (psykologToDelete.billede && !psykologToDelete.billedeUrl.startsWith('/media/')) {
          try {
            const imageRef = ref(storage, `om/${psykologToDelete.billede}`);
            await deleteObject(imageRef);
          } catch (imgError) {
            console.error("Fejl ved sletning af billede:", imgError);
            // Fortsæt alligevel med at slette psykolog
          }
        }
        
        // Opdater psykologer array
        const updatedPsykologer = psykologer.filter(p => p.id !== psykologId);
        setPsykologer(updatedPsykologer);
        
        // Gem opdateret array i Firestore
        await setDoc(doc(db, 'content', 'om'), {
          psykologer: updatedPsykologer,
          lastUpdated: new Date()
        });
        
        setMessage({ type: 'success', text: 'Psykolog blev slettet!' });
      } catch (error) {
        console.error("Fejl ved sletning:", error);
        setMessage({ type: 'error', text: 'Fejl ved sletning af psykolog.' });
      }
    }
  };

  const handleSavePsykolog = async () => {
    try {
      setUploading(true);
      
      // Normaliser indhold før gem
      const normalizedDanishContent = editorRef.current ? normalizeWhitespace(editorRef.current.innerHTML) : '';
      const normalizedEnglishContent = engEditorRef.current ? normalizeWhitespace(engEditorRef.current.innerHTML) : '';
      
      let imageUrl = editedPsykolog.billedeUrl || '';
      let imageName = editedPsykolog.billede || '';
      
      // Generer ID fra navn hvis det er en ny psykolog eller hvis ID er tomt
      const psykologId = isCreating || !editedPsykolog.id 
        ? editedPsykolog.navn.toLowerCase().replace(/\s+/g, '-').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^\w-]/g, '')
        : editedPsykolog.id;
      
      // Upload nyt billede hvis valgt
      if (selectedFile) {
        const fileName = `${psykologId}_${Date.now()}_${selectedFile.name}`;
        const storageRef = ref(storage, `om/${fileName}`);
        await uploadBytes(storageRef, selectedFile);
        imageUrl = await getDownloadURL(storageRef);
        imageName = fileName;
      }
      
      const psykologToSave = {
        ...editedPsykolog,
        id: psykologId,
        billedeUrl: imageUrl,
        billede: imageName,
        link: `/om/${psykologId}`,
        langBeskrivelse: normalizedDanishContent,
        engBeskrivelse: normalizedEnglishContent
      };
      
      let updatedPsykologer = [];
      
      if (isCreating) {
        updatedPsykologer = [...psykologer, psykologToSave];
      } else {
        updatedPsykologer = psykologer.map(psykolog => 
          psykolog.id === editedPsykolog.id ? psykologToSave : psykolog
        );
      }
      
      setPsykologer(updatedPsykologer);
      
      await setDoc(doc(db, 'content', 'om'), {
        psykologer: updatedPsykologer,
        lastUpdated: new Date()
      });
      
      setMessage({ 
        type: 'success', 
        text: isCreating ? 'Psykolog blev oprettet!' : 'Psykolog blev opdateret!' 
      });
      
      setIsEditing(false);
      setIsCreating(false);
      setSelectedPsykolog(null);
      setSelectedFile(null);
      setPreview(null);
    } catch (error) {
      console.error("Fejl ved gem:", error);
      setMessage({ type: 'error', text: 'Fejl ved gem af psykolog.' });
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsCreating(false);
    setSelectedPsykolog(null);
    setSelectedFile(null);
    setPreview(null);
  };

  const closeMessage = () => {
    setMessage({ type: '', text: '' });
  };

  const goBack = () => {
    navigate('/admin');
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      return range;
    }
    return null;
  };

  const restoreSelection = (range) => {
    if (range) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      try {
        selection.addRange(range);
      } catch (e) {
        console.warn('Kunne ikke gendanne selektion:', e);
      }
    }
  };

  const checkActiveFormats = (isEnglish = false) => {
    const setFormats = isEnglish ? setEngActiveFormats : setActiveFormats;
    
    setFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      heading: false, // Vi tjekker dette manuelt senere
      bullet: document.queryCommandState('insertUnorderedList'),
      link: document.queryCommandState('createLink'),
      underline: document.queryCommandState('underline'),
      image: document.queryCommandState('insertImage')
    });
  };

  const handleImageInsertion = async (isEnglish = false) => {
    const editor = isEnglish ? engEditorRef.current : editorRef.current;
    if (!editor) return;
    
    try {
      // Opret en file input og trigger klik på den
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*';
      
      // Lyt efter fil-valg
      fileInput.onchange = async (e) => {
        if (e.target.files && e.target.files[0]) {
          const file = e.target.files[0];
          
          try {
            setUploading(true);
            
            // Upload til Firebase Storage
            const storageRef = ref(storage, `medarbejderBilleder/${Date.now()}_${file.name}`);
            await uploadBytes(storageRef, file);
            const imageUrl = await getDownloadURL(storageRef);
            
            // Fokuser på editoren
            editor.focus();
            
            // Indsæt billede i editoren
            const imgElement = document.createElement('img');
            imgElement.src = imageUrl;
            imgElement.style.maxWidth = '100%';
            imgElement.style.height = 'auto';
            imgElement.style.margin = '1rem 0';
            
            // Få den aktuelle selektion
            const selection = window.getSelection();
            
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              
              // Tjek om selektion er i editoren
              if (editor.contains(range.commonAncestorContainer)) {
                // Indsæt billede ved markørposition
                range.deleteContents();
                range.insertNode(imgElement);
                
                // Opdater HTML-indhold
                if (isEnglish) {
                  setEditedPsykolog(prev => ({
                    ...prev,
                    engBeskrivelse: editor.innerHTML
                  }));
                } else {
                  setEditedPsykolog(prev => ({
                    ...prev,
                    langBeskrivelse: editor.innerHTML
                  }));
                }
              }
            }
          } catch (error) {
            console.error('Fejl ved upload af billede:', error);
            alert('Der opstod en fejl ved upload af billedet. Prøv igen.');
          } finally {
            setUploading(false);
          }
        }
      };
      
      // Simulér klik for at åbne fil-vælgeren
      fileInput.click();
    } catch (error) {
      console.error('Fejl ved håndtering af billede:', error);
    }
  };

  const handleFormatClick = (formatType, isEnglish = false) => {
    const editor = isEnglish ? engEditorRef.current : editorRef.current;
    if (!editor) return;
    
    // Fokuser på editoren hvis den ikke allerede har fokus
    if (!editor.contains(document.activeElement)) {
      editor.focus();
    }

    // Gem den aktuelle selektion
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const selectedText = range.toString();
    
    // Tjek om selektionen er inden for editoren
    if (!editor.contains(range.commonAncestorContainer)) {
      return;
    }

    // Udfør formateringen
    switch (formatType) {
      case 'bold':
        document.execCommand('bold', false);
        break;
      
      case 'italic':
        document.execCommand('italic', false);
        break;
      
      case 'heading':
        const parentElement = range.commonAncestorContainer.parentElement;
        const isHeading = parentElement.tagName === 'H2';
        
        if (isHeading) {
          // Fjern heading
          document.execCommand('formatBlock', false, 'p');
        } else {
          // Tilføj heading
          document.execCommand('formatBlock', false, 'h2');
        }
        break;
      
      case 'bullet':
        document.execCommand('insertUnorderedList', false);
        break;
      
      case 'link':
        const url = selectedText.trim();
        if (url) {
          const validUrl = isValidUrl(url);
          if (validUrl) {
            document.execCommand('createLink', false, validUrl);
          } else {
            const linkUrl = prompt('Indtast URL:', 'https://');
            if (linkUrl && linkUrl.trim() !== '') {
              document.execCommand('createLink', false, linkUrl);
            }
          }
        }
        break;
        
      case 'image':
        handleImageInsertion(isEnglish);
        break;
      
      case 'underline':
        document.execCommand('underline', false);
        break;
      
      default:
        break;
    }

    // Opdater aktive formateringer
    checkActiveFormats(isEnglish);
  };

  const isValidUrl = (text) => {
    try {
      if (text.match(/^(https?:\/\/|www\.)/i)) {
        const url = text.startsWith('www.') ? `https://${text}` : text;
        new URL(url);
        return url;
      }
      return false;
    } catch {
      return false;
    }
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Indlæser...</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="admin-logo">
          <img 
            src="/media/logo/psykologiskperspektiv-logo.avif" 
            alt="Psykologisk Perspektiv" 
            onClick={() => navigate('/admin')}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <div className="admin-header-right">
          <div className="admin-user">
            <span>Logget ind som: </span>
            <strong>{user.email}</strong>
          </div>
          <button onClick={() => navigate('/admin')} className="back-button">
            ← Tilbage
          </button>
        </div>
      </header>
      
      <main className="admin-content">
        <div className="admin-om-container">
          <button className="back-button" onClick={goBack}>
            ← Tilbage til Admin
          </button>
          
          <h1>Administrer Om Os</h1>
          
          {message.text && (
            <div className={`admin-message ${message.type}`}>
              <p>{message.text}</p>
              <button 
                className="close-message" 
                onClick={closeMessage}
              >
                ×
              </button>
            </div>
          )}
          
          {!isEditing && !isCreating && (
            <div className="admin-section">
              <div className="admin-section-header">
                <h2>Psykologer</h2>
                <button 
                  className="create-btn"
                  onClick={handleCreatePsykolog}
                >
                  + Tilføj psykolog
                </button>
              </div>
              
              <p className="admin-description">
                Her kan du administrere psykologerne der vises på "Om os"-siden. 
                Hver psykolog har en egen underside, der viser mere detaljeret information.
              </p>
              
              {psykologer.length === 0 ? (
                <div className="no-items">
                  <p>Der er ingen psykologer at vise. Tilføj en psykolog for at komme i gang.</p>
                </div>
              ) : (
                <div className="psykologer-list">
                  {psykologer.map((psykolog) => (
                    <div key={psykolog.id} className="psykolog-item">
                      <div className="psykolog-preview">
                        {psykolog.billedeUrl && (
                          <div className="psykolog-image">
                            <img src={psykolog.billedeUrl} alt={psykolog.navn} />
                          </div>
                        )}
                        <div className="psykolog-content">
                          <h3>{psykolog.navn}</h3>
                          <p className="psykolog-titler">{psykolog.titler}</p>
                          {psykolog.speciale && (
                            <p className="psykolog-speciale"><strong>{psykolog.speciale}</strong></p>
                          )}
                          <div className="psykolog-kontakt">
                            <p>Email: {psykolog.email}</p>
                            <p>Tlf: {psykolog.telefon}</p>
                          </div>
                          {psykolog.punkter && psykolog.punkter.length > 0 && (
                            <div className="psykolog-punkter">
                              <p><strong>Særlig erfaring og interesse i:</strong></p>
                              <ul>
                                {psykolog.punkter.map((punkt, idx) => (
                                  <li key={idx}>{punkt}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <p className="psykolog-page-link">
                            <strong>Underside:</strong> <a href={psykolog.link} target="_blank" rel="noopener noreferrer">{psykolog.link}</a>
                          </p>
                          <p className="psykolog-has-description">
                            {psykolog.langBeskrivelse ? 
                              <span className="has-description">✓ Har udvidet beskrivelse på undersiden</span> : 
                              <span className="no-description">✗ Mangler udvidet beskrivelse på undersiden</span>
                            }
                            {psykolog.engBeskrivelse && 
                              <span className="has-eng-description">✓ Har engelsk beskrivelse</span>
                            }
                          </p>
                        </div>
                      </div>
                      <div className="psykolog-actions">
                        <button 
                          className="edit-btn"
                          onClick={() => handleEditPsykolog(psykolog)}
                        >
                          Rediger
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDeletePsykolog(psykolog.id)}
                        >
                          Slet
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {(isEditing || isCreating) && (
            <div className="admin-section">
              <h2>{isCreating ? 'Tilføj ny psykolog' : 'Rediger psykolog'}</h2>
              
              <div className="psykolog-form">
                <div className="form-group">
                  <label htmlFor="navn">Navn</label>
                  <input
                    type="text"
                    id="navn"
                    name="navn"
                    value={editedPsykolog.navn}
                    onChange={handleInputChange}
                    onKeyDown={(e) => handleKeyDown(e)}
                    placeholder="Skriv psykologens navn"
                  />
                  <span className="form-help-text">
                    ID genereres automatisk fra navnet: {editedPsykolog.navn ? editedPsykolog.navn.toLowerCase().replace(/\s+/g, '-').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^\w-]/g, '') : ''}
                  </span>
                </div>
                
                <div className="form-group">
                  <label htmlFor="titler">Titler</label>
                  <input
                    type="text"
                    id="titler"
                    name="titler"
                    value={editedPsykolog.titler}
                    onChange={handleInputChange}
                    onKeyDown={(e) => handleKeyDown(e)}
                    placeholder="F.eks. Autoriseret Psykolog, specialist i..."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="engTitler">Titler (engelsk - valgfri)</label>
                  <input
                    type="text"
                    id="engTitler"
                    name="engTitler"
                    value={editedPsykolog.engTitler || ''}
                    onChange={handleInputChange}
                    onKeyDown={(e) => handleKeyDown(e)}
                    placeholder="E.g. Authorized Psychologist, specialist in..."
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="speciale">Speciale (vises med fremhævet tekst)</label>
                  <textarea
                    id="speciale"
                    name="speciale"
                    rows="3"
                    value={editedPsykolog.speciale}
                    onChange={handleInputChange}
                    onKeyDown={(e) => handleKeyDown(e)}
                    placeholder="Speciale i..."
                  ></textarea>
                </div>

                <div className="form-group">
                  <label htmlFor="engSpeciale">Speciale (engelsk - valgfri)</label>
                  <textarea
                    id="engSpeciale"
                    name="engSpeciale"
                    rows="3"
                    value={editedPsykolog.engSpeciale || ''}
                    onChange={handleInputChange}
                    onKeyDown={(e) => handleKeyDown(e)}
                    placeholder="Specialization in..."
                  ></textarea>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={editedPsykolog.email}
                      onChange={handleInputChange}
                      onKeyDown={(e) => handleKeyDown(e)}
                      placeholder="Email"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="telefon">Telefon</label>
                    <input
                      type="text"
                      id="telefon"
                      name="telefon"
                      value={editedPsykolog.telefon}
                      onChange={handleInputChange}
                      onKeyDown={(e) => handleKeyDown(e)}
                      placeholder="Telefonnummer"
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="engEmail">Email (engelsk oversættelse - valgfri)</label>
                    <input
                      type="email"
                      id="engEmail"
                      name="engEmail"
                      value={editedPsykolog.engEmail || ''}
                      onChange={handleInputChange}
                      onKeyDown={(e) => handleKeyDown(e)}
                      placeholder="F.eks. Email:"
                    />
                    <span className="form-help-text">
                      Angiv kun, hvis du vil have en anden tekst end "Email:" på engelsk. Adressen bliver den samme.
                    </span>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="engTelefon">Telefon (engelsk oversættelse - valgfri)</label>
                    <input
                      type="text"
                      id="engTelefon"
                      name="engTelefon"
                      value={editedPsykolog.engTelefon || ''}
                      onChange={handleInputChange}
                      onKeyDown={(e) => handleKeyDown(e)}
                      placeholder="F.eks. Phone:"
                    />
                    <span className="form-help-text">
                      Angiv kun, hvis du vil have en anden tekst end "Tlf:" på engelsk. Nummeret bliver det samme.
                    </span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Billede</label>
                  <div className="image-uploader">
                    {preview && (
                      <div className="image-preview">
                        <img src={preview} alt="Forhåndsvisning" />
                      </div>
                    )}
                    
                    <label className="upload-btn">
                      {preview ? 'Skift billede' : 'Vælg billede'}
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleFileChange}
                        disabled={uploading}
                      />
                    </label>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Særlig erfaring og interesser (punktliste)</label>
                  
                  {editedPsykolog.punkter.length > 0 && (
                    <div className="bullet-list">
                      {editedPsykolog.punkter.map((punkt, index) => (
                        <div key={index} className="bullet-item">
                          <input
                            type="text"
                            value={punkt}
                            onChange={(e) => handleBulletChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e)}
                            placeholder="Punkt"
                          />
                          <button 
                            type="button"
                            className="remove-bullet-btn"
                            onClick={() => handleRemoveBullet(index)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="add-bullet-group">
                    <input
                      type="text"
                      value={newBulletPoint}
                      onChange={(e) => setNewBulletPoint(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e)}
                      placeholder="Tilføj nyt punkt"
                    />
                    <button 
                      type="button"
                      className="add-bullet-btn"
                      onClick={handleAddBullet}
                    >
                      +
                    </button>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Special experience and interests - English (optional)</label>
                  
                  {editedPsykolog.engPunkter && editedPsykolog.engPunkter.length > 0 && (
                    <div className="bullet-list">
                      {editedPsykolog.engPunkter.map((punkt, index) => (
                        <div key={index} className="bullet-item">
                          <input
                            type="text"
                            value={punkt}
                            onChange={(e) => handleEngBulletChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e)}
                            placeholder="Point"
                          />
                          <button 
                            type="button"
                            className="remove-bullet-btn"
                            onClick={() => handleRemoveEngBullet(index)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="add-bullet-group">
                    <input
                      type="text"
                      value={newEngBulletPoint}
                      onChange={(e) => setNewEngBulletPoint(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e)}
                      placeholder="Add new point"
                    />
                    <button 
                      type="button"
                      className="add-bullet-btn"
                      onClick={handleAddEngBullet}
                    >
                      +
                    </button>
                  </div>
                  <span className="form-help-text">
                    Engelske punkter vises kun, når brugeren skifter til engelsk. Hvis der ikke er tilføjet engelske punkter, vises de danske punkter i stedet.
                  </span>
                </div>
                
                <div className="form-group">
                  <label htmlFor="langBeskrivelse">Udvidet beskrivelse (til undersiden)</label>
                  <FormatBar 
                    activeFormats={activeFormats}
                    onFormatClick={(format) => handleFormatClick(format)}
                    showImageButton={true}
                  />
                  <div
                    ref={editorRef}
                    className="rich-text-editor long-description-input"
                    contentEditable
                    onInput={(e) => handleHtmlContentChange(e)}
                    onKeyDown={(e) => handleKeyDown(e)}
                    onFocus={() => handleEditorFocus(false)}
                    data-placeholder="Skriv en udvidet beskrivelse om psykologen her. Denne tekst vises på psykologens egen side."
                  />
                  <span className="form-help-text">
                    Brug formateringsknapperne ovenfor til at formatere teksten.<br />
                    Tryk Enter for at starte et nyt afsnit.
                  </span>
                </div>
                
                <div className="form-group">
                  <label htmlFor="engBeskrivelse">Engelsk beskrivelse (valgfri)</label>
                  <FormatBar 
                    activeFormats={engActiveFormats}
                    onFormatClick={(format) => handleFormatClick(format, true)}
                    showImageButton={true}
                  />
                  <div
                    ref={engEditorRef}
                    className="rich-text-editor long-description-input"
                    contentEditable
                    onInput={(e) => handleHtmlContentChange(e, true)}
                    onKeyDown={(e) => handleKeyDown(e, true)}
                    onFocus={() => handleEditorFocus(true)}
                    data-placeholder="Skriv en engelsk beskrivelse om psykologen her (valgfri)."
                  />
                  <span className="form-help-text">
                    Engelsk beskrivelse vil kun vises, hvis dette felt er udfyldt. Ellers vises kun den danske beskrivelse.
                  </span>
                </div>
                
                <div className="form-actions">
                  <button 
                    className="save-btn"
                    onClick={handleSavePsykolog}
                    disabled={uploading || !editedPsykolog.navn}
                  >
                    {uploading ? 'Gemmer...' : 'Gem psykolog'}
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={handleCancel}
                    disabled={uploading}
                  >
                    Annuller
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      
      <footer className="admin-footer">
        <p>&copy; {new Date().getFullYear()} Psykologisk Perspektiv - Admin Panel</p>
      </footer>
    </div>
  );
};

export default AdminOm;
